import { isPlatformServer } from '@angular/common';
import { PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { catchError, map, mergeMap, of } from 'rxjs';
import { REQUEST, RESPONSE } from 'src/express.tokens';
import { SharryContentStatus, User, UserRole } from 'src/types/graphql';
import { routes } from 'utils/routes';
import { InitUserGQL } from '../user/user.query.generated';
import { SlugContentGQL } from './slug.query.generated';

export const slugGuard: CanActivateFn = (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) => {
  const query = inject(SlugContentGQL);
  const router = inject(Router);
  const platformId = inject(PLATFORM_ID);
  const user$ = inject(InitUserGQL);
  const request = isPlatformServer(platformId) ? inject(REQUEST) : null;
  const response = isPlatformServer(platformId) ? inject(RESPONSE) : null;
  let user: Partial<User>;
  const slug = route.params['slug'];
  if (slug) {
    return user$.fetch({}, { errorPolicy: 'ignore' }).pipe(
      mergeMap(u => {
        user = u.data?.me;
        return query.fetch({ slug });
      }),
      map(r => {
        if (isPlatformServer(platformId)) {
          if (r.data?.sharryContentBySlug?.__typename === 'Post') {
            if (request.i18n?.language !== r.data?.sharryContentBySlug?.originalLanguage) {
              response.setHeader('X-Robots-Tag', 'noindex');
            }
          }
          if (slug !== r.data?.sharryContentBySlug?.slug) {
            if (response) {
              let route: string;
              if (r.data?.sharryContentBySlug?.__typename === 'Post') {
                route = routes[request.i18n?.language as 'it']['routes.community'];
              } else {
                route = routes[request.i18n?.language as 'it']['routes.' + r.data.sharryContentBySlug.contentType];
              }
              response.redirect(301, '/' + route + '/' + r.data.sharryContentBySlug.slug);
            }
          }
        }
        if (!!r.data?.sharryContentBySlug?.id && r.data?.sharryContentBySlug?.contentType === route.data?.contentType) {
          const status = r.data?.sharryContentBySlug?.status;
          if (
            (user && user?.role !== UserRole.StandardUser) ||
            [SharryContentStatus.Published, SharryContentStatus.Suspended, SharryContentStatus.AdminHidden].includes(
              status,
            ) ||
            r.data?.sharryContentBySlug?.owner?.isMe ||
            r.data?.sharryContentBySlug?.creator?.id === user?.id
          ) {
            return true;
          }
          return router.parseUrl('/content-not-found');
        } else {
          return router.parseUrl('/not-found');
        }
      }),
      catchError(e => {
        console.log(e);
        return of(router.parseUrl('/not-found'));
      }),
    );
  } else {
    return router.parseUrl('/not-found');
  }
};
